import { useState } from "react";
import { useGetIdentity, useTranslate } from "ra-core";

import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { DashboardActivityLog } from "@/components/atomic-crm/dashboard/DashboardActivityLog";
import { TaskGroup } from "@/tenax/tasks/TaskRow";
import {
  fullName,
  nativeSelectClass,
  useTaskAreas,
  useTeam,
} from "@/tenax/tasks/taskData";
import { todaySections, toTask, useTodayItems } from "./todayItems";
import { AgreementSignals } from "@/tenax/agreements/AgreementSignals";
import { useAgreementSignals } from "@/tenax/agreements/useAgreementSignals";
import { RacketSignals } from "@/tenax/rackets/RacketSignals";
import { useOverdueRackets } from "@/tenax/rackets/racketHooks";

const SECTIONS = [
  "overdue",
  "today",
  "week",
  "automatic",
  "following",
] as const;

// Today screen (task 1.4, D-032), the start page instead of Atomic's
// dashboard: the tasks of a person by due date, automatic follow-ups, the
// tasks the person follows, alerts. The team view picks the person.
export const TodayPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const { data: team = [] } = useTeam();
  const { data: areas = [] } = useTaskAreas();
  const [personId, setPersonId] = useState<number | null>(null);
  const salesId = personId ?? (identity?.id as number | undefined);
  const { data: items = [], isPending } = useTodayItems(salesId);
  const signals = useAgreementSignals(salesId);
  const overdueRackets = useOverdueRackets(salesId);

  const sections = todaySections(items);
  const byTask = new Map(items.map((item) => [item.task_id, item]));
  const helpers = {
    areaName: (id: number | null) => areas.find((area) => area.id === id)?.name,
    ownerName: (id: number | null) =>
      fullName(team.find((member) => member.id === id)),
    stepsOf: (id: number) => ({
      done: byTask.get(id)?.steps_done ?? 0,
      total: byTask.get(id)?.steps_total ?? 0,
    }),
  };
  const isEmpty =
    !isPending && SECTIONS.every((key) => sections[key].length === 0);

  const content = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="today-person">{translate("tenax.today.person")}</Label>
        <select
          id="today-person"
          className={nativeSelectClass}
          value={salesId ?? ""}
          onChange={(event) => setPersonId(Number(event.target.value))}
        >
          {team.map((member) => (
            <option key={member.id} value={member.id}>
              {member.id === identity?.id
                ? translate("tenax.today.me")
                : fullName(member)}
            </option>
          ))}
        </select>
      </div>

      {isEmpty && (
        <p className="text-muted-foreground text-sm">
          {translate("tenax.today.empty")}
        </p>
      )}

      {SECTIONS.map((key) =>
        sections[key].length > 0 ? (
          <TaskGroup
            key={key}
            title={translate(`tenax.today.sections.${key}`)}
            tasks={sections[key].map(toTask)}
            {...helpers}
          />
        ) : null,
      )}

      <section className="flex flex-col gap-1">
        <h2 className="text-muted-foreground text-sm font-semibold uppercase">
          {translate("tenax.today.sections.signals")}
        </h2>
        {signals.length ? <AgreementSignals signals={signals} /> : null}
        {overdueRackets.length ? (
          <RacketSignals rackets={overdueRackets} />
        ) : null}
        {signals.length || overdueRackets.length ? null : (
          <p className="text-muted-foreground text-sm">
            {translate("tenax.today.no_signals")}
          </p>
        )}
      </section>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader showRefresh>
          <h1 className="text-xl font-semibold">
            {translate("tenax.today.title")}
          </h1>
        </MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 py-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">
          {translate("tenax.today.title")}
        </h1>
        <span className="text-muted-foreground text-sm first-letter:uppercase">
          {new Date().toLocaleDateString("it-IT", {
            weekday: "long",
            day: "numeric",
            month: "long",
            timeZone: "Europe/Rome",
          })}
        </span>
      </div>
      {content}
      <DashboardActivityLog />
    </div>
  );
};
